import React, { useState, useEffect, useRef } from "react";
import { Spin } from "antd";
import { Field, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { Button, Col, Form, Row } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import CustomInput from "../../../CustomControls/CustomInput";
import swal from "sweetalert";
import { post } from "../../../helpers/api_helper";
import {
  POST_GetDepartmentDropDownList,
  POST_GetMaterialCategoryDropDownListByCategoryType,
  POST_GetProductGroupsDetailByID,
  POST_ProductGroupsInsert,
  POST_ProductGroupsUpdate,
} from "../../../helpers/url_helper";
import QuickAddMaterialCategory from "../MaterialCategories/QuickAddMaterialCategory";
import {
  capitalizeString,
  errorMessage,
  successMessage,
} from "../CommonFunctions";

const AddProductGroup = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const { PrimaryIdParam } = useParams();

  const AuthUser = JSON.parse(localStorage.getItem("userDetails"));
  const formsList = AuthUser?.Permissions?.FormList;

  const [
    showQuickAddMaterialCategoryModal,
    setShowQuickAddMaterialCategoryModal,
  ] = useState(false);
  const [categoryType, setCategoryType] = useState(0);
  const [categoryTypeName, setCategoryTypeName] = useState("");

  const scrollToRefTop = (ref) => {
    ref.current.scroll({
      top: 0,
      behavior: "smooth",
    });
  };

  const materialTypeDivRef = useRef();

  const validationSchema = Yup.object().shape({
    ProductGroupName: Yup.string()
      .trim()
      .required("Material Group Name is required field"),
  });

  const formik = useFormik({
    validationSchema,
    onSubmit: (values, formikHelpers) =>
      handleValidSubmit(values, formikHelpers),
    initialValues: {
      ProductGroupIDEncrypt: PrimaryIdParam ? window.atob(PrimaryIdParam) : "",
      ProductGroupName: "",
      ListMaterialType: [],
      ListDepartment: [],
      searchMaterialType: "",
      searchDepartment: "",
    },
    validateOnBlur: false,
  });

  const {
    values: {
      ListMaterialType,
      ListDepartment,
      searchMaterialType,
      searchDepartment,
    },
  } = formik;

  const handleValidSubmit = (values) => {
    setIsLoading(true);
    let payload = {
      ...values,
      MaterialTypeIDs: Array.from(
        values?.ListMaterialType?.filter((e) => e.IsSelected == true),
        (x) => x.MaterialCategoryIDEncrypt
      ).join(","),
      DepatmentIDs: Array.from(
        values?.ListDepartment?.filter((e) => e.IsSelected == true),
        (x) => x.DepartmentIDEncrypt
      ).join(","),
      CompanyIDEncrypt: AuthUser?.CompanyIDEncrypt,
      CreatedByEncrypt: AuthUser?.ReferenceIDEncrypt
        ? AuthUser?.ReferenceIDEncrypt
        : "0",
      ModifiedByEncrypt: AuthUser?.ReferenceIDEncrypt
        ? AuthUser?.ReferenceIDEncrypt
        : "0",
    };
    //console.log("payload: ", payload);

    post(
      PrimaryIdParam ? POST_ProductGroupsUpdate : POST_ProductGroupsInsert,
      payload
    )
      .then((response) => {
        setIsLoading(true);
        if (response?.IsSuccess) {
          // swal("Success!", response?.Message, "success");
          successMessage(response?.Message);
          setTimeout(() => {
            navigate("/product-groups");
          }, 1000);
        } else {
          //swal("Error!", response?.Message, "error");
          errorMessage(response?.Message);
          setIsLoading(false);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (PrimaryIdParam) {
      getProductGroupDetails();
    } else {
      getMaterialTypeList();
      getDepartmentList();
    }
  }, []);

  const getMaterialTypeList = async () => {
    setIsLoading(true);
    await post(POST_GetMaterialCategoryDropDownListByCategoryType, {
      CompanyIDEncrypted: AuthUser?.CompanyIDEncrypt,
      CategoryType: 1, // 1 = Material Type
    })
      .then((response) => {
        if (response?.IsSuccess) {
          formik.setFieldValue(
            "ListMaterialType",
            response?.List?.map((item) => ({
              MaterialCategoryIDEncrypt: item.MaterialCategoryIDEncrypted,
              MaterialTypeName: item.CategoryName,
              IsSelected: false,
              display: true,
            }))
          );
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  const getDepartmentList = async () => {
    setIsLoading(true);
    await post(POST_GetDepartmentDropDownList, {
      CompanyIDEncrypted: AuthUser?.CompanyIDEncrypt,
    })
      .then((response) => {
        if (response?.IsSuccess) {
          formik.setFieldValue(
            "ListDepartment",
            response?.List?.map((item) => ({
              DepartmentIDEncrypt: item.DepartmentIDEncrypted,
              DepartmentName: item.DepartmentName,
              IsSelected: false,
              display: true,
            }))
          );
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  const getProductGroupDetails = () => {
    setIsLoading(true);
    post(POST_GetProductGroupsDetailByID, {
      CompanyIDEncrypt: AuthUser?.CompanyIDEncrypt,
      ProductGroupIDEncrypt: window.atob(PrimaryIdParam),
    })
      .then((response) => {
        if (response?.IsSuccess) {
          formik.setValues({
            ...response,
            ListMaterialType: response?.ListOfProductGroupMaterialType?.map(
              (item) => ({
                ...item,
                display: true,
              })
            ),
            ListDepartment: response?.ListOfProductGroupDepartments?.map(
              (item) => ({
                ...item,
                display: true,
              })
            ),
          });
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  const handleChangeSearchMaterialType = (e) => {
    formik.setFieldValue("searchMaterialType", e?.target?.value);
    formik.setFieldValue(
      "ListMaterialType",
      ListMaterialType?.map((x) => {
        return {
          ...x,
          display: x.MaterialTypeName?.toLowerCase().includes(
            e?.target?.value?.toLowerCase()
          )
            ? true
            : false,
        };
      })
    );
  };

  const handleChangeSearchDepartment = (e) => {
    formik.setFieldValue("searchDepartment", e?.target?.value);
    formik.setFieldValue(
      "ListDepartment",
      ListDepartment?.map((x) => {
        return {
          ...x,
          display: x.DepartmentName?.toLowerCase().includes(
            e?.target?.value?.toLowerCase()
          )
            ? true
            : false,
        };
      })
    );
  };

  const fnCallbackQuickAddMaterialCategory = (categoryID, categoryName) => {
    if (categoryType == 1) {
      // 1 = Material Type
      formik.setFieldValue("ListMaterialType", [
        {
          MaterialCategoryIDEncrypt: categoryID,
          MaterialTypeName: categoryName,
          IsSelected: true,
          display: true,
        },
        ...ListMaterialType,
      ]);
      scrollToRefTop(materialTypeDivRef);
    }
  };

  return (
    <div className="card m-1">
      <div className="card-body p-1">
        <Spin
          size="large"
          spinning={isLoading}
          tip={"Loading..."}
          style={{ position: "fixed" }}
        >
          <div className="tbl-caption p-2">
            <h4 className="heading mb-0">
              {PrimaryIdParam ? "Edit" : "Add"} Material Group
            </h4>
          </div>
          <hr className="mt-0 mb-1"></hr>
          <div className="p-2 pt-0">
            <FormikProvider value={formik}>
              <Form onSubmit={formik.handleSubmit}>
                <Row>
                  <Col lg={4}>
                    <Field
                      label="Material Group Name"
                      name="ProductGroupName"
                      placeholder={"Enter Material Group Name"}
                      component={CustomInput}
                      required
                      maxLength={100}
                      autoFocus
                      onChange={(e) => {
                        formik.setFieldValue(
                          "ProductGroupName",
                          capitalizeString(e)
                        );
                      }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col lg={4} className="mb-2">
                    {AuthUser?.UserType !== 1 ||
                    formsList?.filter((x) => x?.FormID == 17 && x?.IsAdd)
                      ?.length > 0 ? (
                      <Link
                        to="#"
                        className="icon-box icon-box-md bg-primary-light me-1"
                        title="New Material Type"
                        style={{ float: "right" }}
                        onClick={() => {
                          setCategoryType(1); // 1 = Material Type
                          setCategoryTypeName("Material Type");
                          setShowQuickAddMaterialCategoryModal(true);
                        }}
                      >
                        <i className="fa fa-plus"></i>
                      </Link>
                    ) : (
                      ""
                    )}

                    <label className="mb-1">Material Types</label>
                    <div className="card mb-0 w-100">
                      <div className="card-header p-0">
                        <input
                          type="text"
                          name="searchMaterialType"
                          placeholder={"Search here..."}
                          className="form-control form-control-sm"
                          value={searchMaterialType}
                          onChange={handleChangeSearchMaterialType}
                          style={{
                            borderBottomLeftRadius: "0",
                            borderBottomRightRadius: "0",
                          }}
                        ></input>
                      </div>
                      <div
                        ref={materialTypeDivRef}
                        className="card-body p-0 table-responsive"
                        style={{
                          maxHeight: "250px",
                          overflow: "auto",
                        }}
                      >
                        <table className="table table-hover header-border no-footer">
                          <tbody>
                            {ListMaterialType?.filter((x) => x.display == true)
                              ?.length > 0 ? (
                              ListMaterialType?.map((item, indx) => (
                                <tr
                                  key={indx}
                                  style={{
                                    display: item.display == true ? "" : "none",
                                  }}
                                >
                                  <td
                                    style={{
                                      textAlign: "center",
                                      padding: "0.5rem",
                                      width: "15%",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      id={`chkMaterialType${indx}`}
                                      name={`chkMaterialType${indx}`}
                                      checked={item.IsSelected}
                                      onChange={(e) => {
                                        formik.setFieldValue(
                                          `ListMaterialType[${indx}].IsSelected`,
                                          e.target.checked
                                        );
                                      }}
                                    />
                                  </td>
                                  <td
                                    style={{
                                      textAlign: "left",
                                      padding: "0.5rem",
                                      width: "85%",
                                    }}
                                  >
                                    {item.MaterialTypeName}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={2} style={{ textAlign: "center" }}>
                                  <div>{"No options"}</div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </Col>
                  <Col lg={4} className="mb-2">
                    <label className="mb-1">Departments</label>
                    <div className="card mb-0 w-100">
                      <div className="card-header p-0">
                        <input
                          type="text"
                          name="searchDepartment"
                          placeholder={"Search here..."}
                          className="form-control form-control-sm"
                          value={searchDepartment}
                          onChange={handleChangeSearchDepartment}
                          style={{
                            borderBottomLeftRadius: "0",
                            borderBottomRightRadius: "0",
                          }}
                        ></input>
                      </div>
                      <div
                        className="card-body p-0  table-responsive"
                        style={{
                          maxHeight: "250px",
                          overflow: "auto",
                        }}
                      >
                        <table className="table table-hover header-border no-footer">
                          <tbody>
                            {ListDepartment?.filter((x) => x.display == true)
                              ?.length > 0 ? (
                              ListDepartment?.map((item, indx) => (
                                <tr
                                  key={indx}
                                  style={{
                                    display: item.display == true ? "" : "none",
                                  }}
                                >
                                  <td
                                    style={{
                                      textAlign: "center",
                                      padding: "0.5rem",
                                      width: "15%",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      id={`chkDepartment${indx}`}
                                      name={`chkDepartment${indx}`}
                                      checked={item.IsSelected}
                                      onChange={(e) => {
                                        formik.setFieldValue(
                                          `ListDepartment[${indx}].IsSelected`,
                                          e.target.checked
                                        );
                                      }}
                                    />
                                  </td>
                                  <td
                                    style={{
                                      textAlign: "left",
                                      padding: "0.5rem",
                                      width: "85%",
                                    }}
                                  >
                                    {item.DepartmentName}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={2} style={{ textAlign: "center" }}>
                                  <div>{"No options"}</div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </Col>
                </Row>
                <hr className="mt-1 mb-2"></hr>
                <button
                  type="submit"
                  // className="btn btn-primary"
                  className="btn btn-primary btn-sm me-2"
                >
                  <i className="fa-solid fa-floppy-disk"></i> Submit
                </button>
                <button
                  type="button"
                  // className="btn btn-danger light"
                  className="btn btn-danger btn-sm"
                  onClick={(e) => {
                    navigate(-1);
                  }}
                >
                  <i className="fa fa-cancel"></i> Cancel
                </button>
              </Form>
            </FormikProvider>
          </div>
        </Spin>
        <QuickAddMaterialCategory
          categoryType={categoryType}
          showModal={showQuickAddMaterialCategoryModal}
          setShowModal={setShowQuickAddMaterialCategoryModal}
          categoryTypeName={categoryTypeName}
          fnCallback={fnCallbackQuickAddMaterialCategory}
        />
      </div>
    </div>
  );
};

export default AddProductGroup;
